import {
  Button,
  ChevronRightIcon,
  CustomReward,
  CustomRewardCurrency,
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import RewardChildRedeemCard from "./RewardChildRedeemCard"
import { useScreenTimeControlsModalStore } from "./ScreenTimeControlsModal"
import { ScreenTimeControlsModalStep } from "../../../constants"
import { ScreenTimeControlsModalType } from "../../../types/rewards"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { useRouter } from "../../../util/router"
import { localStorageItems } from "../../../util/storage"
import { getScreenTimeConfigurations } from "../../../util/util"

const PurchasedRewardGroup = ({ reward }: { reward: CustomReward }) => {
  const [isHovered, setIsHovered] = useState(false)
  const { onOpen: onOpenScreenTimeControlsModal } =
    useScreenTimeControlsModalStore()
  const router = useRouter()
  const { t } = useTranslation()

  const { numLinkedDevices } = getScreenTimeConfigurations(reward)

  const onRewardClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_CUSTOM_REWARD_DETAILS)
    router.push(`/rewards/details/${reward.id}`)
  }

  const onFinishSetupClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    trackAnalyticEvent(ANALYTIC_EVENTS.REWARDS_TAB_SETUP_SCREENTIME)
    onOpenScreenTimeControlsModal({
      step: ScreenTimeControlsModalStep.INTRO,
      modalType: ScreenTimeControlsModalType.REWARDS_TAB,
      rewardId: reward.id,
    })
  }

  const needsToFinishSetup =
    reward.currency === CustomRewardCurrency.MINUTE &&
    numLinkedDevices === 0 &&
    !localStorage.getItem(
      localStorageItems.screentimeReminderDismissed + reward.id.toString()
    )

  useEffect(() => {
    if (needsToFinishSetup) {
      trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_SCREENTIME_REMINDER_BUTTON, {
        reward_id: reward.id,
      })
    }
  }, []) // eslint-disable-line

  return (
    <div className="w-full flex flex-col border border-borderPrimary rounded-md max-w-[600px]">
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onRewardClick()
          }
        }}
        className="w-full flex flex-col justify-start items-start gap-2 p-4 pr-3 border-b border-borderPrimary rounded-t-md cursor-pointer"
        style={{
          background: isHovered
            ? JoonUIColor.background.xlightGray
            : JoonUIColor.background.primaryNeutral,
          transition: "background 0.1s ease-in-out",
        }}
        onClick={onRewardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="w-full flex flex-row justify-between items-center">
          <div className="w-full flex flex-row gap-2 items-center">
            <Typography
              variant="bodySmallBold"
              textAlign="left"
              style={{
                width: "100%",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {`${reward.emoji} ${reward.title}`}
            </Typography>
            {(!reward.purchasable_from_joonity || reward.date_deleted) && (
              <Typography
                variant="bodyXSmallBold"
                color={JoonUIColor.text.secondary}
                style={{
                  width: "fit-content",
                  whiteSpace: "nowrap",
                  background: JoonColorExpanded.neutral200,
                  padding: `${SPACING.space0} ${SPACING.space2}`,
                  borderRadius: SPACING.space2,
                }}
              >
                {reward.date_deleted
                  ? t("REWARD_DELETED")
                  : t("REWARD_NOT_AVAILABLE")}
              </Typography>
            )}
          </div>
          <ChevronRightIcon color={JoonUIColor.icon.neutral} size={24} />
        </div>
        {needsToFinishSetup && (
          <Button
            text={t("REWARD_FINISH_SETUP")}
            size="medium"
            onMouseEnter={() => setIsHovered(false)}
            onMouseLeave={() => setIsHovered(true)}
            onClick={onFinishSetupClick}
            fullWidth
          />
        )}
      </div>

      {numLinkedDevices > 0 && (
        <Typography
          variant="bodyXSmall"
          color={JoonUIColor.text.secondary}
          style={{
            width: "100%",
            background: JoonUIColor.background.primaryNeutral,
            padding: `${SPACING.space1} ${SPACING.space4}`,
            borderBottom: `1px solid ${JoonUIColor.border.default}`,
          }}
        >
          {t("REWARD_LINKED_ON_DEVICES", { count: numLinkedDevices })}
        </Typography>
      )}

      <FlexBox direction="column" gap={SPACING.space0}>
        {reward.assignments.map((assignment, index) => (
          <RewardChildRedeemCard
            childId={assignment.user_id}
            key={assignment.user_id}
            reward={reward}
            isGrouped={true}
            style={{
              background: JoonUIColor.background.primaryNeutral,
              borderRadius: 0,
              border: "none",
              borderBottomLeftRadius:
                index === reward.assignments.length - 1 ? SPACING.space2 : 0,
              borderBottomRightRadius:
                index === reward.assignments.length - 1 ? SPACING.space2 : 0,
              borderBottom:
                index === reward.assignments.length - 1
                  ? "none"
                  : `1px solid ${JoonUIColor.border.default}`,
            }}
          />
        ))}
      </FlexBox>
    </div>
  )
}

export default PurchasedRewardGroup
